import cn from 'classnames'
import { ISalesTeamCoverage } from 'legacy/lut/classes/models/salesTeam'
import { fetchSalesTeamCoverageByPartnerId } from 'legacy/lut/services/salesTeam'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { IPartner } from 'clickNgo/entities/partner/models'
import { Carousel } from 'cng-common/components/carousel'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Tooltip } from 'core-components/library/tooltip'

import { AccordionToggle } from './components/accordionToggle'
import { ConnectionIcon } from './components/connectionIcon'
import { MAIL_TO, MS_TEAM_CALL, MS_TEAM_CHAT } from './configs'
import { getSortedAgents } from './helpers'
import { companySalesTeamTranslation } from './translation'

import styles from './CompanySalesTeam.module.scss'

type Props = {
  partner: IPartner
}

export const CompanySalesTeam = memo<Props>(
  ({ partner }) => {
    const [isLoading, setIsLoading] = useState(false)
    const [agents, setAgents] = useState<ISalesTeamCoverage[]>([])
    const [activeIndex, setActiveIndex] = useState(0)
    const [isExpanded, setIsExpanded] = useState(false)

    const isEmpty = useMemo(() => agents.length === 0, [agents])

    const selectedAgent = useMemo(() => agents[activeIndex] || null, [activeIndex, agents])

    const countSize = useMemo(() => {
      if (agents.length > 99) {
        return '8px'
      } else if (agents.length > 9) {
        return '12px'
      } else {
        return '14px'
      }
    }, [agents])

    const selectedAgentEmail = useMemo(
      () => (selectedAgent ? selectedAgent.email : 'me'),
      [selectedAgent]
    )

    const emptySalesTeamContent = useMemo(
      () => (
        <div className={cn(styles.card, styles.empty)}>
          <h3 className={styles.mockTitle}>
            <Translation text={companySalesTeamTranslation.emptySalesTeamContent} />
          </h3>
        </div>
      ),
      []
    )

    const emailTooltip = useCallback(
      () => (
        <Translation
          text={companySalesTeamTranslation.email}
          replaceParams={[{ key: '{email}', value: selectedAgentEmail }]}
        />
      ),
      [selectedAgentEmail]
    )
    const msTeamCallTooltip = useCallback(
      () => (
        <Translation
          text={companySalesTeamTranslation.msTeamCall}
          replaceParams={[{ key: '{email}', value: selectedAgentEmail }]}
        />
      ),
      [selectedAgentEmail]
    )
    const msTeamChatTooltip = useCallback(
      () => (
        <Translation
          text={companySalesTeamTranslation.msTeamChat}
          replaceParams={[{ key: '{email}', value: selectedAgentEmail }]}
        />
      ),
      [selectedAgentEmail]
    )

    const renderAgentCard = useCallback((agent: ISalesTeamCoverage) => {
      const name =
        agent?.firstName || agent?.lastName
          ? `${agent.firstName || '-'} ${agent.lastName || '-'}`
          : '-'
      const role = agent?.roles ? agent.roles : '-'
      const bUnit = agent?.businessUnit ? agent.businessUnit : '-'
      const assignmentPeriod = agent?.assignmentPeriod ? agent.assignmentPeriod : '-'

      return (
        <Carousel.Item key={agent.email}>
          <div className={styles.card}>
            <div className={styles.leadPBM}>
              <RenderWhen condition={Boolean(agent?.isLeadPBM)}>
                <i className="kbase kbase-star-alt" />
                <Translation text={companySalesTeamTranslation.leadPBM} />
              </RenderWhen>
            </div>
            <div className={styles.item}>
              <div className={styles.label}>
                <Translation text={companySalesTeamTranslation.name} />
              </div>
              <div className={styles.value}>{name}</div>
            </div>

            <div className={styles.item}>
              <div className={styles.label}>
                <Translation text={companySalesTeamTranslation.role} />
              </div>
              <div className={styles.value}>{role}</div>
            </div>
            <div className={styles.item}>
              <div className={styles.label}>
                <Translation text={companySalesTeamTranslation.bUnit} />
              </div>
              <div className={styles.value}>{bUnit}</div>
            </div>
            <div className={styles.item}>
              <div className={styles.label}>
                <Translation text={companySalesTeamTranslation.assignmentPeriod} />
              </div>
              <div className={styles.value}>{assignmentPeriod}</div>
            </div>
          </div>
        </Carousel.Item>
      )
    }, [])

    const renderAgents = useCallback(() => {
      if (isEmpty) {
        return <Carousel.Item>{emptySalesTeamContent}</Carousel.Item>
      } else {
        return agents.map(renderAgentCard)
      }
    }, [agents, emptySalesTeamContent, isEmpty, renderAgentCard])

    useEffect(() => {
      setIsLoading(true)
      fetchSalesTeamCoverageByPartnerId(partner.topPartnerId)
        .then((response) => setAgents(getSortedAgents(response)))
        .finally(() => {
          setIsLoading(false)
          setActiveIndex(0)
          setIsExpanded(true)
        })
    }, [partner.topPartnerId])

    return (
      <div className={styles.container}>
        <LoaderOverlay isVisible={isLoading} />
        <div className={styles.header}>
          <div className={styles.titleContainer}>
            <h3 className={styles.title}>
              <Translation text={companySalesTeamTranslation.title} />
            </h3>
            <div className={styles.count} style={{ fontSize: countSize }}>
              {agents.length}
            </div>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <AccordionToggle isOpen={isExpanded} onChange={setIsExpanded} />
            </div>
          </div>
          <div className={styles.connections}>
            <RenderWhen condition={!isEmpty}>
              <Tooltip tipContent={emailTooltip}>
                <ConnectionIcon
                  href={`${MAIL_TO}${selectedAgent?.email}`}
                  isDisabled={isEmpty}
                  iconClassName="hpicon hpicon-user-email"
                />
              </Tooltip>
              <Tooltip tipContent={msTeamCallTooltip}>
                <ConnectionIcon
                  href={`${MS_TEAM_CALL}${selectedAgent?.email}`}
                  isDisabled={isEmpty}
                  iconClassName="kbase kbase-ask-an-expert"
                />
              </Tooltip>
              <Tooltip tipContent={msTeamChatTooltip}>
                <ConnectionIcon
                  href={`${MS_TEAM_CHAT}${selectedAgent?.email}`}
                  isDisabled={isEmpty}
                  iconClassName="kbase kbase-chat"
                />
              </Tooltip>
            </RenderWhen>
            <RenderWhen condition={isEmpty}>
              <span className={styles.empty}>
                <i className="kbase kbase-lvl-users" />
                <Translation text={companySalesTeamTranslation.emptySalesTeamContent} />
              </span>
            </RenderWhen>
          </div>
        </div>
        <div className={cn(styles.content, { [styles.expanded]: isExpanded })}>
          <Carousel selectedIndex={activeIndex} onChange={setActiveIndex} hasIndicators>
            {renderAgents()}
          </Carousel>
        </div>
      </div>
    )
  },
  (prevProps, nextProps) => prevProps.partner.partnerId === nextProps.partner.partnerId
)

CompanySalesTeam.displayName = 'CompanySalesTeam'
